
import {
  Component,
  ElementRef,
  HostListener,
  OnInit,
  ViewChild,
} from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { TranslateService, LangChangeEvent } from '@ngx-translate/core';
import { ArticlesService } from 'src/app/services/articles/articles.service';
import { ImageService } from 'src/app/services/image/image.service';
import { LocalizationService } from 'src/app/services/localization/localization.service';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-artical-details',
  templateUrl: './artical-details.component.html',
  styleUrls: ['./artical-details.component.css'],
})
export class ArticalDetailsComponent implements OnInit {
  article: any;
  articleId: any = '';
  currentContent: any = null;
  userLang = '';
  imagePath = environment.apiUrl;

  // Dynamic content from translations
  displayTitle = '';
  displaySubtitle = '';
  displaySections: any[] = [];
  sectionImageUrls: { [key: string]: string } = {};

  @ViewChild('mainHeader', { static: false }) mainHeaderRef!: ElementRef;
  private styleElement: HTMLStyleElement | null = null;

  constructor(
    private articleService: ArticlesService,
    private imageService: ImageService,
    private route: ActivatedRoute,
    private metaService: Meta,
    private titleService: Title,
    private translate: TranslateService,
    private localizationService: LocalizationService
  ) {
    this.userLang = this.localizationService.getUserLang();
  }

  ngOnInit(): void {
    this.route.paramMap.subscribe((params) => {
      this.articleId = params.get('id');

      if (this.articleId) {
        this.articleService.getArticleById(this.articleId).subscribe({
          next: (data) => {
            this.article = data;

            // Load content based on current language
            this.loadArticleContent();
            this.addMetaTags();
          },
          error: (err) => console.error(err),
        });
      }
    });

    // Listen for language changes
    this.translate.onLangChange.subscribe((event: LangChangeEvent) => {
      if (this.article) {
        this.userLang = event.lang;
        this.loadArticleContent();
      }
    });
  }

  loadArticleContent(): void {
    if (!this.article) return;

    // Get the translation for current language
    const translation = this.article.translations?.find(
      (t: any) => t.languageCode === this.userLang
    );

    if (translation) {
      this.displayTitle = translation.title;
      this.displaySubtitle = translation.subtitle;
    } else {
      // Fallback to default values
      this.displayTitle = this.article.title;
      this.displaySubtitle = this.article.subtitle;
    }

    // Sort sections by sectionNumber
    const sortedSections = [...(this.article.sections || [])].sort(
      (a, b) => a.sectionNumber - b.sectionNumber
    );

    // Get section content based on language
    this.displaySections = sortedSections.map((section: any) => {
      const sectionTranslation = section.translations?.find(
        (t: any) => t.languageCode === this.userLang
      );

      return {
        id: section.id,
        text: sectionTranslation?.text || section.text,
        image: section.sectionImage,
        sectionNumber: section.sectionNumber,
      };
    });
  }

  getSectionImageUrl(imagePath: string): string {
    // console.log(this.article.mainImage);
    // console.log(this.imageService.getImagePath(imagePath));
    let imagePathUpdated =
      this.imageService.getArticleImagePath(imagePath) ||
      'assets/placeholder.png';

    return this.imageService.handleImageUrl(imagePathUpdated);
  }

  addMetaTags(): void {
    if (!this.article) return;

    const translation = this.article.translations?.find(
      (t: any) => t.languageCode === this.userLang
    );

    const metaTitle = translation?.metaTitle || this.article.metaTitle;
    const metaDescription =
      translation?.metaDescription || this.article.metaDescription;
    const metaKeywords = translation?.metaKeywords || this.article.metaKeywords;

    this.titleService.setTitle(metaTitle);

    this.metaService.updateTag({
      name: 'description',
      content: metaDescription,
    });

    this.metaService.updateTag({
      name: 'keywords',
      content: metaKeywords,
    });

    this.metaService.updateTag({
      property: 'og:title',
      content: metaTitle,
    });

    this.metaService.updateTag({
      property: 'og:description',
      content: metaDescription,
    });

    if (this.article.mainImage) {
      this.metaService.updateTag({
        property: 'og:image',
        content: this.imagePath + this.article.mainImage,
      });
    }
  }

  ngAfterViewInit() {
    if (document.fonts) {
      document.fonts.ready.then(() => {
        setTimeout(() => this.positionHeaderLine(), 100);
      });
    } else {
      setTimeout(() => this.positionHeaderLine(), 500);
    }
  }

  ngOnDestroy() {
    if (this.styleElement) {
      this.styleElement.remove();
    }

    // Clean up blob URLs
    Object.values(this.sectionImageUrls).forEach((url) => {
      URL.revokeObjectURL(url);
    });
  }

  @HostListener('window:resize', ['$event'])
  onResize() {
    setTimeout(() => this.positionHeaderLine(), 100);
  }

  private positionHeaderLine(): void {
    if (!this.mainHeaderRef?.nativeElement) return;

    const header = this.mainHeaderRef.nativeElement;
    const headerStyle = window.getComputedStyle(header);

    const tempElement = document.createElement('div');
    tempElement.style.cssText = `
      position: absolute;
      visibility: hidden;
      white-space: nowrap;
      font-family: ${headerStyle.fontFamily};
      font-size: ${headerStyle.fontSize};
      font-weight: ${headerStyle.fontWeight};
      letter-spacing: ${headerStyle.letterSpacing};
    `;
    document.body.appendChild(tempElement);

    const words = header.textContent.trim().split(' ');
    const headerWidth = header.offsetWidth;
    let lines: string[] = [];
    let currentLine = '';

    for (let word of words) {
      const testLine = currentLine ? currentLine + ' ' + word : word;
      tempElement.textContent = testLine;

      if (tempElement.offsetWidth <= headerWidth) {
        currentLine = testLine;
      } else {
        if (currentLine) {
          lines.push(currentLine);
          currentLine = word;
        } else {
          lines.push(word);
          currentLine = '';
        }
      }
    }

    if (currentLine) {
      lines.push(currentLine);
    }

    document.body.removeChild(tempElement);

    if (lines.length > 0) {
      const lastLine = lines[lines.length - 1];

      const measureElement = document.createElement('div');
      measureElement.style.cssText = `
        position: absolute;
        visibility: hidden;
        white-space: nowrap;
        font-family: ${headerStyle.fontFamily};
        font-size: ${headerStyle.fontSize};
        font-weight: ${headerStyle.fontWeight};
        letter-spacing: ${headerStyle.letterSpacing};
      `;
      measureElement.textContent = lastLine;
      document.body.appendChild(measureElement);

      const lastLineWidth = measureElement.offsetWidth;
      const totalWidth = header.offsetWidth;
      document.body.removeChild(measureElement);

      const startPosition = (totalWidth - lastLineWidth) / 2;

      if (this.styleElement) {
        this.styleElement.remove();
      }

      this.styleElement = document.createElement('style');
      this.styleElement.textContent = `
        .main-header::after {
          left: ${startPosition}px !important;
        }
      `;
      document.head.appendChild(this.styleElement);
    }
  }
}
